import React, { PropsWithChildren } from 'react'
import { toast } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useDeleteUserPermissionsMutation } from 'queries/admin/userPermissions'
import { useUserPermissionsWorkflowsContextValue } from '../../../context/UserPermissionsWorkflowsProvider'

type RemoveUserPermissionsFlowContextState = {
  state: {
    isPending: boolean
    isError: boolean
    errorMessage: string | undefined
  }
  actions: {
    confirm: () => void
  }
}

const [Context, useRemoveUserPermissionsFlowContextValue] =
  createContext<RemoveUserPermissionsFlowContextState>({
    name: 'RemoveUserPermissionsFlowContext',
  })

export const RemoveUserPermissionsFlowProvider = ({
  children,
}: PropsWithChildren) => {
  const { removeUserPermissionsFlow } =
    useUserPermissionsWorkflowsContextValue()
  const { mutate, isPending, isError, error } =
    useDeleteUserPermissionsMutation()

  const value: RemoveUserPermissionsFlowContextState = {
    state: {
      isPending,
      isError,
      errorMessage: parseErrorMessage(error),
    },
    actions: {
      confirm: () => {
        const { target, close } = removeUserPermissionsFlow
        if (!target) return

        const request =
          target.kind === 'permission'
            ? { permissionId: target.permissionId, companyScopeId: null }
            : { permissionId: null, companyScopeId: target.companyScopeId }

        mutate(request, {
          onSuccess: () => {
            toast.success(
              target.kind === 'permission'
                ? 'Permission revoked successfully'
                : 'Company scope revoked successfully',
              { position: 'top-center' }
            )
            close()
          },
        })
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useRemoveUserPermissionsFlowContextValue }
